import { I18n } from "@assets/strings";
import React, { useState } from "react";
import { useHistory } from "@core/routing/useHistory";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { CurrentProfile } from "@domain/currentProfile/currentProfile";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { useFilterByUserSlug } from "@domain/filter/hooks";
import { Media } from "@domain/media/media";
import { combineAvatarWithFilter, handleUploadFileError } from "@utils/file";
import { useAsync } from "@utils/hooks";
import { ClosableModal } from "@ui/modals/closableModal";
import { gap } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";
import { Tooltipable } from "@ui/tooltips/tooltipable";
import { DefaultButton } from "@ui/components/buttons/defaultButton";
import { ProfilePicture } from "./profilePicture";

interface OpenModalPictureProps extends React.HTMLAttributes<HTMLDivElement> {
	close: () => void;
	imageProfile?: Media;
	profileSlug?: string;
}
export const OpenModalPicture: React.FC<OpenModalPictureProps> = ({
	close,
	imageProfile,
	profileSlug,
	...htmlProps
}) => {
	const { result: filter } = useFilterByUserSlug(profileSlug as string);
	const [loading, setLoading] = useState(false);
	const currentProfile = useCurrentProfile();
	const { currentProfileService, mediaService } = useServices();

	const { run } = useAsync(
		(newProfile: CurrentProfile) => currentProfileService.saveProfileInfos(newProfile),
		[currentProfile]
	);

	const history = useHistory();

	return (
		<Container keyboardEventDisabled close={close} isCorpProfilePicture isTransparent {...htmlProps}>
			<ProfilePicture picture={imageProfile} size="XXL" shadowed />
			{filter && (
				<>
					{loading ? (
						<ClipLoader />
					) : (
						<>
							{!currentProfile?.avatarCrop ? (
								<Tooltipable text={I18n.t("profile.edit.crop.error_message", { size: 250 })} offset={-2} dark={true}>
									<StyledButton lighter disabled>
										{I18n.t("profile.edit.crop.add_filter_from_another_profile.title")}
									</StyledButton>
								</Tooltipable>
							) : (
								<StyledButton
									lighter
									onClick={async (event) => {
										event.stopPropagation();
										if (!currentProfile?.avatarCrop) {
											return;
										}

										try {
											setLoading(true);
											const file = await combineAvatarWithFilter(currentProfile.avatarCrop, filter.asset);
											const media = await mediaService.uploadMedia(file);
											const newProfile = await run({ ...currentProfile, avatar: media, filter: filter });

											createToast(
												I18n.t("success"),
												I18n.t("profile.edit.crop.add_filter_from_another_profile.message"),
												"success"
											);
											history.push(Path.Profile(newProfile));
										} catch (error) {
											handleUploadFileError(error as Error);
										} finally {
											setLoading(false);
											close();
										}
									}}
								>
									{I18n.t("profile.edit.crop.add_filter_from_another_profile.title")}
								</StyledButton>
							)}
						</>
					)}
				</>
			)}
		</Container>
	);
};

const Container = styled(ClosableModal)`
	display: flex;
	width: auto;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	${gap(10)};
	:first-child :first-child svg {
		display: none;
	}
	padding: 0px;
`;

const StyledButton = styled(DefaultButton)`
	color: ${({ theme }) => theme.colors.primary};

	&:disabled {
		pointer-events: visible;
	}
`;
